'use client';

import { usePathname } from 'next/navigation';
import React from 'react';

interface Props {
  children: React.ReactNode;
  renderErrorScreen: (error?: Error) => React.ReactNode;
  onError?: (error: Error) => void;
}

interface PropsWithPathname extends Props {
  pathname: string | null;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorPathname?: string | null;
}

class ErrorBoundary extends React.PureComponent<PropsWithPathname, State> {
  state: State = {
    hasError: false,
  };

  static getDerivedStateFromProps(props: PropsWithPathname, state: State) {
    if (state.hasError && state.errorPathname) {
      if (props.pathname !== state.errorPathname) {
        return { hasError: false, error: undefined, errorPathname: undefined };
      }
    }

    return null;
  }

  componentDidCatch(error: Error) {
    this.setState({ hasError: true, error, errorPathname: this.props.pathname });
    this.props.onError?.(error);
  }

  render() {
    if (this.state.hasError) {
      return this.props.renderErrorScreen(this.state.error);
    }

    return this.props.children;
  }
}

const WrappedErrorBoundary = (props: Props) => {
  const pathname = usePathname();
  return <ErrorBoundary { ...props } pathname={ pathname }/>;
};

export default React.memo(WrappedErrorBoundary);
